import React from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Box,
	Typography,
	LinearProgress,
	CircularProgress,
	IconButton,
	Collapse
} from "@mui/material";
import { Close, CheckCircle, Error, Info } from "@mui/icons-material";

interface ImportStatusModalProps {
	open: boolean;
	onClose: () => void;
	status: "idle" | "importing" | "success" | "error";
	progress?: {
		currentChunk: number;
		totalChunks: number;
		processedUsers: number;
		totalUsers: number;
	};
	results?: {
		totalProcessed: number;
		totalChunks: number;
		errors: any[];
		customVariablesCount: number;
		duplicatesFiltered?: number;
		uniqueUsersAdded?: number;
	};
	error?: string;
}

export function ImportStatusModal({
	open,
	onClose,
	status,
	progress,
	results,
	error
}: ImportStatusModalProps) {
	const getStatusIcon = () => {
		switch (status) {
			case "importing":
				return <CircularProgress size={24} />;
			case "success":
				return <CheckCircle color="success" />;
			case "error":
				return <Error color="error" />;
			default:
				return <Info color="primary" />;
		}
	};

	const getStatusTitle = () => {
		switch (status) {
			case "importing":
				return "Importando Usuários...";
			case "success":
				return "Importação Concluída!";
			case "error":
				return "Erro na Importação";
			default:
				return "Status da Importação";
		}
	};

	const getProgressPercentage = () => {
		if (!progress || progress.totalChunks === 0) return 0;
		return (progress.currentChunk / progress.totalChunks) * 100;
	};

	const getStatusColor = () => {
		switch (status) {
			case "success":
				return "success";
			case "error":
				return "error";
			default:
				return "primary";
		}
	};

	return (
		<Dialog
			open={open}
			onClose={status === "importing" ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			disableEscapeKeyDown={status === "importing"}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={2}>
					{getStatusIcon()}
					<Typography variant="h6">{getStatusTitle()}</Typography>
					<Box flexGrow={1} />
					{status !== "importing" && (
						<IconButton onClick={onClose} size="small">
							<Close />
						</IconButton>
					)}
				</Box>
			</DialogTitle>

			<DialogContent>
				{status === "importing" && progress && (
					<Box>
						<Typography variant="body2" color="text.secondary" gutterBottom>
							Processando lote {progress.currentChunk} de {progress.totalChunks}
						</Typography>
						<LinearProgress
							variant="determinate"
							value={getProgressPercentage()}
							sx={{ mb: 2 }}
							color={getStatusColor() as any}
						/>
						<Typography variant="body2" color="text.secondary">
							{progress.processedUsers} de {progress.totalUsers} usuários processados
						</Typography>
						<Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
							⏳ Isso pode levar alguns minutos para planilhas grandes...
						</Typography>
					</Box>
				)}

				{status === "success" && results && (
					<Box>
						<Typography variant="body1" gutterBottom>
							✅ Importação concluída com sucesso!
						</Typography>
						
						<Box sx={{ mt: 2, p: 2, bgcolor: "success.light", borderRadius: 1 }}>
							<Typography variant="subtitle2" gutterBottom>
								📊 Estatísticas da Importação:
							</Typography>
							<Typography variant="body2">
								• <strong>{results.totalProcessed}</strong> usuários processados
							</Typography>
							<Typography variant="body2">
								• <strong>{results.totalChunks}</strong> lotes processados
							</Typography>
							<Typography variant="body2">
								• <strong>{results.customVariablesCount}</strong> variáveis personalizadas detectadas
							</Typography>
							{results.duplicatesFiltered !== undefined && results.duplicatesFiltered > 0 && (
								<Typography variant="body2" color="warning.main">
									• <strong>{results.duplicatesFiltered}</strong> duplicatas filtradas (já existiam na campanha)
								</Typography>
							)}
							{results.uniqueUsersAdded !== undefined && (
								<Typography variant="body2" color="success.main">
									• <strong>{results.uniqueUsersAdded}</strong> usuários únicos adicionados à campanha
								</Typography>
							)}
						</Box>

						<Typography variant="body2" sx={{ mt: 2, color: "success.main" }}>
							👥 {results.uniqueUsersAdded !== undefined ? 
								`${results.uniqueUsersAdded} usuários únicos foram adicionados à lista de destinatários da campanha!` :
								'Todos os usuários foram adicionados à lista de destinatários da campanha!'
							}
						</Typography>
					</Box>
				)}

				{status === "error" && (
					<Box>
						<Typography variant="body1" color="error" gutterBottom>
							❌ Erro na importação
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{error || "Erro desconhecido"}
						</Typography>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				{status === "importing" && (
					<Button disabled>
						<CircularProgress size={16} sx={{ mr: 1 }} />
						Processando...
					</Button>
				)}
				{status !== "importing" && (
					<Button onClick={onClose} variant="contained">
						{status === "success" ? "Continuar" : "Fechar"}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
}

export default ImportStatusModal;
